import _get from 'lodash/get';
import _isEmpty from 'lodash/isEmpty';
import _sortBy from 'lodash/sortBy';
import _debounce from 'lodash/debounce';
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import I18n from 'i18n-js';
import classnames from 'classnames/bind';
import { Form, Row, Col, Icon, Tooltip } from 'grab-ui';
import CustomerInfo from 'components/v2/customer-info/customer-info';
import SelectField from 'components/v2/select-field/select-field';
import PaymentField from 'components/booking/payment-field/payment-field';
import InputField from 'components/v2/input-field/input-field';
import OrderDetailsBoard from 'components/v2/order-details-board/order-details-board';
import DivButton from 'components/v2/div-button/div-button';
import TaxiTypeTNC from 'components/v2/taxi-type-tnc';
import circledCheckMark from 'public/icons/circled-check-mark.svg';
import {
  getSenderInfo,
  getRecipientInfo,
  getSelectedServiceType,
  checkCODServiceInQuote,
  checkCODService,
  hasCODFieldValue,
  generateLocalisedItemOptions,
  getQuoteToRender,
  getQuotesToRender,
} from 'helper/order';
import { getCompanyID } from 'helper/user';
import { trackClick, event, cat, custom } from 'common/utils/instrumentation';
import calcPrice from 'common/utils/calc-price';
import needRenderTaxiTypeTNC from 'common/utils/taxi-type-tnc';
import {
  CASH_BY_SENDER,
  DOODLE_VALUE,
  PAYMENT_DISPLAY_NAMES,
  POSTPAID_BILLING_TYPE,
  QUOTE_OPTIMIZED,
} from 'constants/booking-constants';
import selectFieldCss from 'components/v2/select-field/select-field.less';
import {
  scribeTrackEvent,
  getCommonFacet,
  SCRIBE_STATE_NAMES,
  SCRIBE_DELIVERY_DETAILS_EVENTS,
  SCRIBE_EVENT_PARAMS,
} from 'applications/scribe/instrumentations/track';

import { getTotalAmountFromQuotes, getAmount } from 'helper/quote';

import { FIELD_NAME, MODE, FORM_FIELD_OPTIONS } from './constants';
import css from './book-order-form.less';
import {
  trackBookOrderForm,
  scribeTrackBookOrderForm,
} from './book-order-form-tracking';
import { SHOPIFY_PREFIX, SHOPIFY_MODE } from '../shopify-home/constants';

const cx = classnames.bind(css);

class BookOrderForm extends Component {
  constructor(props) {
    super(props);
    this.state = {
      isCollapsed: false,
      isShopify: props.mode.startsWith(SHOPIFY_PREFIX),
    };

    this.setupItemOptions();
  }

  onToggleForm = () => {
    const { isCollapsed } = this.state;
    const { userInfo, index } = this.props;

    const label = isCollapsed ? 'EXPAND' : 'COLLAPSE';
    trackClick(event.DELIVERY_DETAILS_CLICKS, cat.GE_DELIVERY_DETAILS, label, {
      [custom.COMPANY_ID]: getCompanyID(userInfo),
    });
    scribeTrackEvent({
      stateName: SCRIBE_STATE_NAMES.EXPRESS_DELIVERY_DETAILS_WEB,
      eventName: isCollapsed
        ? SCRIBE_DELIVERY_DETAILS_EVENTS.EXPAND
        : SCRIBE_DELIVERY_DETAILS_EVENTS.COLLAPSE,
      facets: {
        ...getCommonFacet(userInfo),
        [SCRIBE_EVENT_PARAMS.DELIVERY_NUMBER]: index,
      },
    });

    this.setState(prevState => ({
      isCollapsed: !prevState.isCollapsed,
    }));
  };

  onEditDelivery = () => {
    const { onEditAllDropoffLocations } = this.props;
    onEditAllDropoffLocations();
  };

  onClickAddStop = () => {
    const { order: { recipientLocations } } = this.props;
    const { onClickDropoffLocation } = this.props;
    onClickDropoffLocation(recipientLocations.length)();
  };

  onRemovePromo = () => {
    const { form: { setFieldsValue } } = this.props;
    // this will go through onFieldsChange -> trigger updateOrder -> trigger fetch quote again
    setFieldsValue({
      [FIELD_NAME.PROMO]: '',
    });
  };

  setupItemOptions = () => {
    const { userInfo, pushNotification } = this.props;
    this.itemOptions = generateLocalisedItemOptions({
      userInfo,
      pushNotification,
    }).map(({ id, iString }) => ({
      value: `${id}`,
      title: iString,
      disabled: false,
      child: iString,
    }));
  };

  getRecipientCount = () => {
    const { order } = this.props;
    const recipientCount = _get(order, 'recipientLocations', []).filter(
      rl => rl.deleted !== true,
    ).length;
    return recipientCount;
  };

  getServiceTypesToRender = hasCODValue => {
    const { order } = this.props;

    if (!order || !order.serviceTypes) return [];

    const recipientCount = this.getRecipientCount();

    return order.serviceTypes.map(serviceType => {
      const { ID, name, maxMSDStops } = serviceType;
      const disabled =
        maxMSDStops < recipientCount ||
        (hasCODValue && !checkCODService(serviceType.cashOnDelivery));
      const childComponent = (
        <div>
          {name}
          {maxMSDStops < recipientCount && (
            <span className={selectFieldCss.disableMessage}>
              <br />
              {I18n.t('v2.serviceTypeSelect.disabledMessage.stopExceeded', {
                maxMSDStops,
              })}
            </span>
          )}
        </div>
      );
      return {
        title: name,
        value: ID.toString(),
        child: childComponent,
        disabled,
      };
    });
  };

  getSelectedServiceID = () => {
    const { order } = this.props;
    const serviceTypes = _get(order, 'serviceTypes', []);
    const initialServiceID = _get(order[FIELD_NAME.SERVICE_TYPE], 'value');

    const isServiceIDMatched = serviceTypes.some(
      service => service.ID.toString() === initialServiceID,
    );
    return isServiceIDMatched
      ? initialServiceID
      : _get(order, 'serviceTypes[0].ID', '').toString();
  };

  getSelectedPaymentID = () => {
    const { order, payments, isCashlessPaymentDisabled } = this.props;
    const currentSelection = _get(order[FIELD_NAME.PAYMENT_METHOD], 'value');
    if (isCashlessPaymentDisabled) {
      return CASH_BY_SENDER;
    }
    if (currentSelection && currentSelection !== DOODLE_VALUE) {
      return currentSelection;
    }
    const validPayments = payments.filter(p => p.enabled);
    const sortedValidPayments = _sortBy(validPayments, p => p.isPrimary);
    const firstPaymentID = _get(sortedValidPayments, '[0].paymentTypeID');
    return firstPaymentID ? firstPaymentID.toString() : CASH_BY_SENDER;
  };

  // TODO move to helper/history-order.js
  getPaymentDisplayName = (order, payments) => {
    const paymentID =
      _get(order, `[${FIELD_NAME.PAYMENT_METHOD}].value`) || CASH_BY_SENDER;

    if (!_isEmpty(PAYMENT_DISPLAY_NAMES[paymentID])) {
      return I18n.t(PAYMENT_DISPLAY_NAMES[paymentID]);
    }

    const payment = payments.find(p => p.paymentTypeID === paymentID);
    if (_isEmpty(payment)) return I18n.t(PAYMENT_DISPLAY_NAMES[CASH_BY_SENDER]);

    if (payment.type === POSTPAID_BILLING_TYPE) {
      return I18n.t(PAYMENT_DISPLAY_NAMES[POSTPAID_BILLING_TYPE]);
    }

    return payment.type;
  };

  renderPromoParams = () => {
    const { order } = this.props;
    const promo = _get(order[FIELD_NAME.PROMO], 'value');

    if (_isEmpty(promo) || order.fetchingQuote) {
      return {
        showPromoErrorMessage: false,
        promoSuffix: null,
      };
    }

    const isPromoApplied = _get(order, 'quote.forRender.isDiscountApplied');
    return isPromoApplied
      ? {
          showPromoErrorMessage: false,
          promoSuffix: (
            <Icon type="check-circle" theme="twoTone" twoToneColor="#00b14f" />
          ),
        }
      : {
          showPromoErrorMessage: true,
          promoSuffix: (
            <Icon
              type="close-circle"
              theme="twoTone"
              twoToneColor="#ee6352"
              onClick={this.onRemovePromo}
            />
          ),
        };
  };

  renderBulkFormActions = () => {
    const { isCollapsed, isShopify } = this.state;
    const {
      order,
      orders,
      onDeleteBulkForm,
      onCopyBulkForm,
      allowCopy,
    } = this.props;
    const orderQty = orders ? orders.length : 0;
    const currentOrderFetchingQuote = _get(order, 'fetchingQuote', false);

    /*
     * Hide delete order button unless more than 1 order
     * Hide copy/ delete buttons during quote fetching to avoid bugs in slow network condition
    */
    const showDeleteButton =
      !currentOrderFetchingQuote && orderQty && orderQty > 1;
    const showCopyButton = !currentOrderFetchingQuote && !isShopify;
    return (
      <div className={css.titleActions}>
        {showDeleteButton && (
          <DivButton className={css.actionButton} onClick={onDeleteBulkForm}>
            <img
              className={css.actionIcon}
              style={{ width: 19 }}
              src={require('public/icons/delete.svg')}
              alt=""
            />
          </DivButton>
        )}
        {showCopyButton && (
          <DivButton
            className={cx('actionButton', { copyButtonDisabled: !allowCopy })}
            onClick={onCopyBulkForm}
          >
            <img
              className={css.actionIcon}
              src={require('public/icons/copy.svg')}
              alt=""
            />
          </DivButton>
        )}
        <DivButton className={css.actionButton} onClick={this.onToggleForm}>
          <img
            className={css.actionIcon}
            src={
              isCollapsed
                ? require('public/icons/expand.svg')
                : require('public/icons/collapse.svg')
            }
            alt=""
          />
        </DivButton>
      </div>
    );
  };

  renderExpandedForm = isHidden => {
    const {
      mode,
      order,
      orders,
      payments,
      userInfo,
      isCashPaymentDisabled,
      isCashlessPaymentDisabled,
      onClickPickupLocation,
      onClickDropoffLocation,
      onDeleteRecipient,
      form,
      onRetryFailedPayments,
      enableRouteOptimization,
      index: orderIndex,
    } = this.props;
    const { isShopify } = this.state;
    const isBulkMode = mode === MODE.MULTI_BOOK || isShopify;
    const hasCODValue = hasCODFieldValue(order);
    const isCODServiceSelected = checkCODServiceInQuote(order);
    const serviceTypesToRender = this.getServiceTypesToRender(hasCODValue);
    const selectedServiceID = this.getSelectedServiceID();
    const selectedServiceType = getSelectedServiceType(order);
    const minCODAmount = calcPrice(
      _get(selectedServiceType, 'cashOnDelivery.lowerLimit', null),
      _get(selectedServiceType, 'cashOnDelivery.currency', null),
    );
    const maxCODAmount = calcPrice(
      _get(selectedServiceType, 'cashOnDelivery.upperLimit', null),
      _get(selectedServiceType, 'cashOnDelivery.currency', null),
    );

    let disableAddStop = false;
    // Bulk book mode does not support MSD (via bulk upload)
    if (isBulkMode) {
      disableAddStop = true;
    } else {
      // Do not show add stops if already hit maximum stops supported
      const recipientCount = this.getRecipientCount();
      const multiStopMaxStopCount = _get(selectedServiceType, 'maxMSDStops', 1);
      disableAddStop = recipientCount >= multiStopMaxStopCount;
    }

    const { allowCash, allowCard } = order.serviceTypes
      ? order.serviceTypes.find(s => s.ID === Number(selectedServiceID)) || {}
      : {};
    const promoParams = this.renderPromoParams();
    const { countryCode } = userInfo.selectedCity;
    const senderInfo = getSenderInfo(order);
    let recipientInfo = getRecipientInfo(order);
    if (_isEmpty(recipientInfo)) {
      recipientInfo = { 0: {} }; // empty recipient for bulk booking flow
    }

    const defaultPaymentID = this.getSelectedPaymentID();

    const renderTaxiTypeTNC = needRenderTaxiTypeTNC(
      countryCode,
      selectedServiceType,
    );

    const quote = isBulkMode
      ? getQuotesToRender(orders)
      : getQuoteToRender(order);
    const totalAmount = isBulkMode
      ? getTotalAmountFromQuotes(quote)
      : getAmount(quote);

    const isRouteOptimized =
      _get(this.props, 'optimizeQuotesStatus.status', '') === QUOTE_OPTIMIZED;

    return (
      <div className={isHidden ? css.hidden : null}>
        <div
          key={0}
          className={cx('pickupAndDropoff', {
            'pickupAndDropoff-bulk': isBulkMode,
          })}
        >
          <h5 className={css.title}>
            {I18n.t('v2.homepage.manualOrder.heading.deliveryRoute')}
          </h5>
          <CustomerInfo
            info={senderInfo}
            idSuffix={'sender'}
            isSender
            countryCode={countryCode}
            onClickLocation={onClickPickupLocation}
            form={form}
            isViewMode={isShopify}
          />
          {Object.keys(recipientInfo).map((key, index, recipients) => (
            <CustomerInfo
              key={key}
              info={recipientInfo[key]}
              idSuffix={`recipient-${key}`}
              isSender={false}
              countryCode={countryCode}
              onClickLocation={
                enableRouteOptimization
                  ? this.onEditDelivery
                  : onClickDropoffLocation(key)
              }
              form={form}
              onDelete={isBulkMode ? null : onDeleteRecipient(key)}
              disableDeletion={isBulkMode}
              index={index}
              noOfRecipients={recipients.length}
              renderCOD={isCODServiceSelected}
              minCODAmount={minCODAmount}
              maxCODAmount={maxCODAmount}
              isViewMode={isShopify}
              shopifyFulfillmentOrder={
                mode === SHOPIFY_MODE.SHOPIFY_BOOK_MULTIPLE
                  ? _get(order, 'fulfillmentOrder')
                  : _get(order, `fulfillmentOrders[${key}]`)
              }
              isContextualOnboardingEnabled={orderIndex === 0 && index === 0}
            />
          ))}
          {enableRouteOptimization ? (
            <React.Fragment>
              {isShopify &&
                isRouteOptimized && (
                  <div className={css.routeOptimizedText}>
                    <img src={circledCheckMark} alt={''} />
                    {I18n.t('v2.locationDrawer.routeAlreadyShortened')}
                  </div>
                )}

              <Tooltip
                placement="bottomLeft"
                title={I18n.t('v2.homepage.manualOrder.editDeliveryTooltip')}
              >
                <span
                  className={css.addStop}
                  onClick={this.onEditDelivery}
                  role="button"
                  tabIndex="0"
                  onKeyDown={null}
                >
                  {I18n.t('v2.homepage.manualOrder.button.editDelivery')}
                </span>
              </Tooltip>
            </React.Fragment>
          ) : (
            !disableAddStop && (
              <span
                className={css.addStop}
                onClick={this.onClickAddStop}
                role="button"
                tabIndex="0"
                onKeyDown={null}
              >
                {`+ ${I18n.t('v2.homepage.manualOrder.button.addStop')}`}
              </span>
            )
          )}
        </div>
        <div
          key={1}
          className={cx('orderOptions', { 'orderOptions-bulk': isBulkMode })}
        >
          <div>
            <h5 className={css.optionTitle}>
              {I18n.t('v2.homepage.manualOrder.heading.deliveryOptions')}
            </h5>
            <Row type="flex" gutter={{ md: 16 }}>
              <Col md={8} xs={renderTaxiTypeTNC ? 21 : 24}>
                <SelectField
                  id={FIELD_NAME.SERVICE_TYPE}
                  label=""
                  className={cx({
                    // only grey out, still clickable
                    'serviceType-shallow-disabled':
                      hasCODValue && !isCODServiceSelected,
                  })}
                  options={serviceTypesToRender}
                  fieldOptions={{
                    initialValue: selectedServiceID,
                  }}
                  form={form}
                />
              </Col>
              {renderTaxiTypeTNC && (
                <Col md={1} xs={3} className={css.taxiTypeTNCWrapper}>
                  <TaxiTypeTNC serviceType={selectedServiceType} />
                </Col>
              )}
            </Row>
          </div>
          <div>
            <h5 className={css.optionTitle}>
              {I18n.t('v2.homepage.manualOrder.heading.itemDetails')}
            </h5>
            <Row type="flex" gutter={{ md: 16 }}>
              <Col md={8} xs={24}>
                <InputField
                  id={FIELD_NAME.ITEM_WEIGHT}
                  type="number"
                  suffix="kg"
                  placeholder={I18n.t(
                    'v2.homepage.manualOrder.placeholder.weight',
                  )}
                  fieldOptions={{
                    initialValue: _get(order[FIELD_NAME.ITEM_WEIGHT], 'value'),
                    rules: [
                      {
                        required: true,
                        type: 'number',
                        min: 0.01,
                        message: I18n.t(
                          'booking.validateMessage.itemWeightMin',
                        ),
                      },
                    ],
                    getValueFromEvent: e => {
                      const value = Number(e.currentTarget.value);
                      return isNaN(value) ? Number(value) : value;
                    },
                  }}
                  form={form}
                />
              </Col>
              <Col md={8} xs={24}>
                <SelectField
                  id={FIELD_NAME.ITEM_TYPE}
                  options={this.itemOptions}
                  placeholder={I18n.t(
                    'v2.homepage.manualOrder.placeholder.itemType',
                  )}
                  fieldOptions={{
                    initialValue: _get(order[FIELD_NAME.ITEM_TYPE], 'value'),
                  }}
                  form={form}
                />
              </Col>
              <Col md={8} xs={24}>
                <InputField
                  id={FIELD_NAME.INSTRUCTION}
                  placeholder={I18n.t(
                    'v2.homepage.manualOrder.placeholder.instruction',
                  )}
                  fieldOptions={{
                    initialValue: _get(order[FIELD_NAME.INSTRUCTION], 'value'),
                    ...FORM_FIELD_OPTIONS[FIELD_NAME.INSTRUCTION],
                  }}
                  form={form}
                />
              </Col>
            </Row>
          </div>
          <div>
            <h5 className={css.optionTitle}>
              {I18n.t('v2.homepage.manualOrder.heading.paymentDetails')}
            </h5>
            <Row type="flex" gutter={{ md: 16 }}>
              <Col md={8} xs={24} className={css.paymentField}>
                <PaymentField
                  id={FIELD_NAME.PAYMENT_METHOD}
                  label=""
                  paymentMethods={payments}
                  initialValue={defaultPaymentID}
                  allowCash={allowCash}
                  allowCard={allowCard}
                  serviceTypes={order.serviceTypes}
                  dropdownClassName={css.paymentFieldDropdown}
                  optionClassName={css.paymentOption}
                  isCashPaymentDisabled={isCashPaymentDisabled}
                  isCashlessPaymentDisabled={isCashlessPaymentDisabled}
                  form={form}
                  onRetryFailedPayments={onRetryFailedPayments}
                  totalAmount={totalAmount}
                />
              </Col>
              <Col md={8} xs={24}>
                <InputField
                  id={FIELD_NAME.REFERENCE_CODE}
                  placeholder={I18n.t(
                    'v2.homepage.manualOrder.placeholder.referenceCode',
                  )}
                  fieldOptions={{
                    ...FORM_FIELD_OPTIONS[FIELD_NAME.REFERENCE_CODE],
                  }}
                  form={form}
                />
              </Col>
              <Col md={8} xs={24}>
                <InputField
                  id={FIELD_NAME.PROMO}
                  placeholder={I18n.t(
                    'v2.homepage.manualOrder.placeholder.promo',
                  )}
                  suffix={_get(promoParams, 'promoSuffix')}
                  fieldOptions={{
                    ...FORM_FIELD_OPTIONS[FIELD_NAME.PROMO],
                  }}
                  form={form}
                />
                {_get(promoParams, 'showPromoErrorMessage', false) && (
                  <div
                    className={css.promoFeedback}
                    role="button"
                    onKeyDown={null}
                    tabIndex="0"
                    onClick={this.onRemovePromo}
                  >
                    {/* TODO: Remove click action text */}
                    <span>{I18n.t('v2.error.invalidPromo')}</span>
                  </div>
                )}
              </Col>
            </Row>
          </div>
        </div>
      </div>
    );
  };

  // eslint-disable-next-line arrow-body-style
  renderCollapsedForm = isHidden => {
    const { order, payments } = this.props;
    const senderInfo = getSenderInfo(order);
    const recipientInfo = _get(getRecipientInfo(order), '[0]', {});

    return (
      <div className={isHidden ? css.hidden : null}>
        <OrderDetailsBoard
          pickUpLocationKeyword={_get(senderInfo, 'keywords', '')}
          senderName={_get(senderInfo, 'name', '')}
          senderPhone={_get(senderInfo, 'phone', '')}
          dropOffLocationKeyword={_get(recipientInfo, 'keywords', '')}
          receiverName={_get(recipientInfo, 'name', '')}
          receiverPhone={_get(recipientInfo, 'phone', '')}
          serviceType={_get(getSelectedServiceType(order), 'name', '')}
          paymentMethod={this.getPaymentDisplayName(order, payments)}
          vehicleType={
            /* TODO: What are we supposed to do with this selection?
            *       It is hard-coded in the UI at the moment
            *       https://grab.slack.com/archives/CESFL6Y1H/p1610527419400100
            */
            I18n.t('v2.homepage.manualOrder.button.bike')
          }
          referenceCode={_get(order[FIELD_NAME.REFERENCE_CODE], 'value')}
          itemWeight={_get(order[FIELD_NAME.ITEM_WEIGHT], 'value')}
        />
      </div>
    );
  };

  render() {
    const { mode, index } = this.props;
    const { isCollapsed, isShopify } = this.state;
    const isBulkMode = mode === MODE.MULTI_BOOK || isShopify;

    return (
      <div className={cx('orderForm', { 'orderForm-bulk': isBulkMode })}>
        {isBulkMode && (
          <div className={cx('formTitle-bulk')}>
            <h5 className={css.title}>
              {mode === SHOPIFY_MODE.SHOPIFY_BOOK_MSD
                ? I18n.t('shopify.orders.createBookingPage.MSD.heading')
                : I18n.t('v2.homepage.manualOrder.heading.deliveryNo', {
                    no: index + 1,
                  })}
            </h5>
            {this.renderBulkFormActions()}
          </div>
        )}
        {isBulkMode && this.renderCollapsedForm(!isCollapsed)}
        {this.renderExpandedForm(isBulkMode && isCollapsed)}
      </div>
    );
  }
}

BookOrderForm.propTypes = {
  mode: PropTypes.string.isRequired,
  order: PropTypes.shape({}),
  payments: PropTypes.arrayOf(PropTypes.shape({})),
  onClickPickupLocation: PropTypes.func,
  onClickDropoffLocation: PropTypes.func,
  onEditAllDropoffLocations: PropTypes.func,
  onDeleteRecipient: PropTypes.func,
  // eslint-disable-next-line react/no-unused-prop-types
  updateOrder: PropTypes.func,
  form: PropTypes.shape({}).isRequired,
  // For bulk booking forms
  orders: PropTypes.arrayOf(PropTypes.shape({})),
  index: PropTypes.number.isRequired,
  onDeleteBulkForm: PropTypes.func,
  onCopyBulkForm: PropTypes.func,
  userInfo: PropTypes.shape({}).isRequired,
  allowCopy: PropTypes.bool.isRequired,
  isCashPaymentDisabled: PropTypes.bool.isRequired,
  isCashlessPaymentDisabled: PropTypes.bool.isRequired,
  pushNotification: PropTypes.func.isRequired,
  onRetryFailedPayments: PropTypes.func.isRequired,
  enableRouteOptimization: PropTypes.bool,
};

BookOrderForm.defaultProps = {
  order: {},
  orders: [],
  payments: [],
  index: 0,
  onClickPickupLocation: () => {},
  onClickDropoffLocation: () => {},
  onEditAllDropoffLocations: () => {},
  updateOrder: () => {},
  onDeleteBulkForm: () => {},
  onCopyBulkForm: () => {},
  onDeleteRecipient: () => {},
  enableRouteOptimization: false,
};

const onFieldsChange = _debounce((props, changedValues, allValues) => {
  const { mode, order, userInfo, index } = props;
  const companyID = getCompanyID(userInfo);

  trackBookOrderForm(changedValues, companyID);
  scribeTrackBookOrderForm({
    changedValues,
    allValues,
    index,
    userInfo,
  });

  const shouldDeepUpdate = Object.keys(changedValues).every(
    key =>
      key === FIELD_NAME.SERVICE_TYPE ||
      key === FIELD_NAME.PAYMENT_METHOD ||
      key === FIELD_NAME.PROMO ||
      // Setting payment method sometimes not triggering getQuotes as
      // this function is debounced and called from multiple places
      (allValues[FIELD_NAME.PAYMENT_METHOD].value &&
        !order[FIELD_NAME.PAYMENT_METHOD]),
  ); // change other fields such as name, phone, cod, ... dont not trigger any follow action

  // Dont need to trigger anything if the changes come from Book Again action: [name, phone, cod, note] by setFieldValues
  const isChangeFromUI = Object.keys(changedValues).length === 1;
  if (!shouldDeepUpdate && !isChangeFromUI) return;

  if (mode === MODE.SINGLE_BOOK) {
    const { updateOrder, updateOrderShallow } = props;
    const newOrder = {
      ...order,
      ...allValues,
    };

    (shouldDeepUpdate ? updateOrder : updateOrderShallow)(newOrder);
  }

  if (mode === MODE.MULTI_BOOK || mode.startsWith(SHOPIFY_PREFIX)) {
    const { index, updateBulkOrder, updateBulkOrderShallow } = props;
    const newOrder = {
      ...order,
      ...allValues,
    };
    (shouldDeepUpdate || mode.startsWith(SHOPIFY_PREFIX)
      ? updateBulkOrder
      : updateBulkOrderShallow)({
      index,
      order: newOrder,
    });
  }
}, 300);

export default Form.create({ onFieldsChange })(BookOrderForm);
